import React, { useMemo } from 'react';
import { Button, Chip } from '@heroui/react';
import { Icon } from '@iconify/react';
import { Typography } from '../../../../../components';
import { ProposalData } from '../../types';
import { useTranslations } from '../../../../../contexts/translation.context';
import {
  PartnerOfferInfoResponse,
  PartnerOfferStatsPeriod,
} from '../../../../../api/client/clientCplService';

type PeriodKey = 'last_30_days' | 'from_30_to_60_days' | 'more_than_60_days';

const PERIOD_ORDER: PeriodKey[] = ['last_30_days', 'from_30_to_60_days', 'more_than_60_days'];

const PERIOD_HEADERS: Record<PeriodKey, React.ReactNode> = {
  last_30_days: (
    <div className="flex items-center gap-1.5 w-[7.5rem] text-xxs font-medium text-auth-muted leading-5">
      <span>&lt;</span>
      <span>30 дней</span>
    </div>
  ),
  from_30_to_60_days: (
    <div className="flex items-center gap-1.5 w-[7.5rem] text-xxs font-medium text-auth-muted leading-5">
      <span>30 - 60 дней</span>
    </div>
  ),
  more_than_60_days: (
    <div className="flex items-center gap-1.5 w-[7.5rem] text-xxs font-medium text-auth-muted leading-5">
      <span className="rotate-180 inline-block scale-y-[-1]">&lt;</span>
      <span>60 дней</span>
    </div>
  ),
};

const UsersIcon = () => (
  <Icon
    icon="solar:users-group-two-rounded-linear"
    width={16}
    height={16}
    className="text-auth-muted"
  />
);
const MessageIcon = () => (
  <Icon icon="solar:chat-round-line-linear" width={16} height={16} className="text-auth-muted" />
);
const MoneyIcon = () => (
  <Icon icon="solar:banknote-linear" width={16} height={16} className="text-auth-muted" />
);
const DiagramIcon = () => (
  <Icon icon="solar:diagram-up-linear" width={16} height={16} className="text-auth-muted" />
);
const PieChartIcon = () => (
  <Icon icon="solar:pie-chart-2-linear" width={16} height={16} className="text-auth-muted" />
);
const LinkIcon = () => (
  <Icon icon="solar:square-top-up-linear" width={16} height={16} className="text-auth-muted" />
);
const CartIcon = () => (
  <Icon
    icon="solar:cart-large-minimalistic-linear"
    width={16}
    height={16}
    className="text-auth-muted"
  />
);
const CalendarIcon = () => (
  <Icon icon="solar:calendar-linear" width={16} height={16} className="text-auth-muted" />
);
const PenIcon = () => (
  <Icon icon="solar:pen-new-square-linear" width={16} height={16} className="text-auth-muted" />
);

const formatNumber = (value?: number | null, isLoading?: boolean) => {
  if (value === null || value === undefined) {
    return isLoading ? '—' : '0';
  }
  return value.toLocaleString();
};

const formatCurrency = (value?: string | number | null, isLoading?: boolean) => {
  if (value === null || value === undefined) {
    return isLoading ? '—' : '$0.00';
  }
  const numericValue = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(numericValue)) return '—';
  return `$${numericValue.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  })}`;
};

const formatPercent = (value?: number | null) => {
  if (value === null || value === undefined) return '—';
  return `${value}%`;
};

const getTimeAgo = (dateString: string, t: (key: string) => string) => {
  // Если сервер отдает локальное время с 'Z', нужно добавить offset
  const utcDate = new Date(dateString);
  if (Number.isNaN(utcDate.getTime())) {
    return '—';
  }
  const date = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000);

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  if (diffMs < 0) {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  const diffInMinutes = Math.floor(diffMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInMonths = Math.floor(diffInDays / 30);

  if (diffInMonths > 0) {
    const remainingDays = diffInDays % 30;
    const template = t('proposals.timeAgo.monthsDays');
    return template
      .replace('{{months}}', diffInMonths.toString())
      .replace('{{days}}', remainingDays.toString());
  }

  if (diffInDays > 0) {
    const template = t('proposals.timeAgo.daysHours');
    return template
      .replace('{{days}}', diffInDays.toString())
      .replace('{{hours}}', (diffInHours % 24).toString());
  }

  if (diffInHours > 0) {
    const template = t('proposals.timeAgo.hours');
    return template.replace('{{hours}}', diffInHours.toString());
  }

  if (diffInMinutes > 0) {
    const template = t('proposals.timeAgo.minutes');
    return template.replace('{{minutes}}', diffInMinutes.toString());
  }

  return t('proposals.timeAgo.justNow');
};

const buildPeriodValues = (
  data: Record<PeriodKey, PartnerOfferStatsPeriod | null>,
  formatter: (period: PartnerOfferStatsPeriod | null) => string
): string[] => PERIOD_ORDER.map((key) => formatter(data[key]));

const MetricRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  values: string[];
  hint?: string;
}> = ({ icon, label, values, hint }) => (
  <div className="flex items-center justify-between w-full h-4">
    <div className="flex gap-2 items-center max-md:w-[65%]">
      {icon}
      <span className="text-xs font-normal text-auth-muted leading-4">{label}</span>
      {hint && (
        <div className="flex items-center gap-1.5">
          <div className="w-[0.1875rem] h-[0.1875rem] rounded-full bg-auth-strength-none"></div>
          <span className="text-xxs font-normal text-neutral-8 leading-4">{hint}</span>
        </div>
      )}
    </div>
    <div className="flex gap-2 items-center text-xs font-normal text-auth-ink leading-4 max-md:w-[35%]">
      {values.map((value, index) => (
        <div key={`${label}-${index}`} className="w-[7.5rem]">
          {value}
        </div>
      ))}
    </div>
  </div>
);

const OrdersStatusChip: React.FC<{ color: string; value: string }> = ({ color, value }) => (
  <Chip
    variant="bordered"
    classNames={{
      base: 'border-[1.4px] gap-1 border-auth-strength-none h-6 py-0 px-2',
      content: 'p-0',
    }}
    startContent={<div className={`w-2 h-2 rounded-full ${color}`}></div>}
  >
    <span className="text-xs font-normal text-auth-ink leading-4">{value}</span>
  </Chip>
);

interface OfferTabProps {
  proposal: ProposalData;
  onClose: () => void;
  offerInfo: PartnerOfferInfoResponse | null;
  isLoading: boolean;
}

const OfferTab: React.FC<OfferTabProps> = ({ proposal, onClose, offerInfo, isLoading }) => {
  const { t } = useTranslations();

  const periodData = useMemo<Record<PeriodKey, PartnerOfferStatsPeriod | null>>(
    () => ({
      last_30_days: offerInfo?.last_30_days ?? null,
      from_30_to_60_days: offerInfo?.from_30_to_60_days ?? null,
      more_than_60_days: offerInfo?.more_than_60_days ?? null,
    }),
    [offerInfo]
  );

  const statusCounts = offerInfo?.status_counts ?? null;
  const ordersTotal = statusCounts
    ? statusCounts.waiting +
      statusCounts.in_progress +
      statusCounts.completed +
      statusCounts.canceled
    : 0;
  const completedPercent =
    ordersTotal > 0 && statusCounts ? Math.round((statusCounts.completed / ordersTotal) * 100) : 0;

  const ordersData = {
    waiting: statusCounts?.waiting ?? 0,
    inProgress: statusCounts?.in_progress ?? 0,
    completed: statusCounts?.completed ?? 0,
    completedPercent,
    rejected: statusCounts?.canceled ?? 0,
  };

  const completedPercentDisplay =
    ordersTotal > 0 ? formatPercent(ordersData.completedPercent) : '—';

  const metrics = [
    {
      icon: <UsersIcon />,
      label: t('proposals.offerDetails.subscribers'),
      hint: t('proposals.offerDetails.subscribersHint'),
      values: buildPeriodValues(periodData, (period) =>
        formatNumber(period?.subscribers, isLoading)
      ),
    },
    {
      icon: <MessageIcon />,
      label: t('proposals.offerDetails.messageConversion'),
      hint: t('proposals.offerDetails.messageConversionHint'),
      values: buildPeriodValues(periodData, () => '—'),
    },
    {
      icon: <MoneyIcon />,
      label: 'ARPU',
      hint: t('proposals.offerDetails.arpuHint'),
      values: buildPeriodValues(periodData, (period) =>
        formatCurrency(period?.subscribers_price, isLoading)
      ),
    },
    {
      icon: <DiagramIcon />,
      label: 'ROMI',
      hint: t('proposals.offerDetails.romiHint'),
      values: buildPeriodValues(periodData, () => '—'),
    },
    {
      icon: <PieChartIcon />,
      label: t('proposals.offerDetails.purchases'),
      hint: '',
      values: buildPeriodValues(periodData, (period) =>
        formatNumber(period?.transactions, isLoading)
      ),
    },
  ];

  const orderChips = [
    { key: 'waiting', color: 'bg-warning', value: formatNumber(ordersData.waiting, isLoading) },
    {
      key: 'inProgress',
      color: 'bg-partner',
      value: formatNumber(ordersData.inProgress, isLoading),
    },
    {
      key: 'completed',
      color: 'bg-success',
      value: `${formatNumber(ordersData.completed, isLoading)}(${completedPercentDisplay})`,
    },
    {
      key: 'rejected',
      color: 'bg-auth-strength-weak',
      value: formatNumber(ordersData.rejected, isLoading),
    },
  ];

  const getSourceName = (source: string) => {
    const sourceMap: { [key: string]: string } = {
      only_fans: 'OF Search Engine',
      instagram: 'Instagram',
      reddit: 'Reddit',
      tiktok: 'TikTok',
      google: 'Google',
      telegram: 'Telegram',
      twitter: 'Twitter',
      youtube: 'YouTube',
      another: t('proposals.other'),
    };
    return sourceMap[source] || source;
  };

  return (
    <div className="flex flex-col">
      {/* Statistics Section */}
      <div className="border-b border-auth-surface-hover px-4 py-4 max-md:overflow-x-auto scroller-none">
        <div className="flex flex-col gap-2 max-md:shrink-0 max-md:w-[45rem]">
          {/* Period Headers Row */}
          <div className="flex items-center justify-between w-full">
            <div className="h-5 max-md:w-[65%]"></div>
            <div className="flex gap-2 items-center max-md:w-[35%]">
              {PERIOD_ORDER.map((key) => (
                <React.Fragment key={key}>{PERIOD_HEADERS[key]}</React.Fragment>
              ))}
            </div>
          </div>

          {metrics.map((metric) => (
            <MetricRow key={metric.label} {...metric} />
          ))}
        </div>
      </div>

      {/* Additional Info Section */}
      <div className="px-4 py-4 flex flex-col gap-4 md:items-end max-md:overflow-x-auto scroller-none">
        <div className="flex flex-col gap-2 w-full max-md:shrink-0 max-md:w-[50rem]">
          {/* Source Row */}
          <div className="flex gap-4 items-center w-full">
            <div className="flex gap-2 items-center w-[29.1875rem] max-md:shrink-0">
              <LinkIcon />
              <span className="text-xs font-normal text-auth-muted leading-4">
                {t('proposals.offerDetails.source')}
              </span>
            </div>
            <div className="border-[1.4px] border-solid border-auth-strength-none flex items-center gap-1 h-6 px-2 rounded-3.5">
              {proposal.partner.platformIcon}
              <span className="text-xs font-normal text-auth-ink leading-4">
                {getSourceName(proposal.campaign.source)}
              </span>
            </div>
          </div>

          {/* Orders Row */}
          <div className="flex gap-4 items-center w-full">
            <div className="flex gap-2 items-center w-[29.1875rem] max-md:shrink-0">
              <div className="flex gap-2 items-center">
                <CartIcon />
                <span className="text-xs font-normal text-auth-muted leading-4">
                  {t('proposals.offerDetails.orders')}
                </span>
              </div>
              <div className="flex gap-2 items-start">
                <div className="flex items-center gap-1.5">
                  <div className="w-1 h-1 rounded-full bg-warning"></div>
                  <span className="text-xxs font-normal text-neutral-8 leading-4">
                    {t('proposals.partnerDetails.waiting')}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-1 h-1 rounded-full bg-partner"></div>
                  <span className="text-xxs font-normal text-neutral-8 leading-4">
                    {t('proposals.partnerDetails.inProgress')}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-1 h-1 rounded-full bg-success"></div>
                  <span className="text-xxs font-normal text-neutral-8 leading-4">
                    {t('proposals.partnerDetails.completed')}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-1 h-1 rounded-full bg-auth-strength-weak"></div>
                  <span className="text-xxs font-normal text-neutral-8 leading-4">
                    {t('proposals.partnerDetails.rejected')}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 items-start flex-wrap">
              {orderChips.map((chip) => (
                <OrdersStatusChip key={chip.key} color={chip.color} value={chip.value} />
              ))}
            </div>
          </div>

          {/* Created Row */}
          <div className="flex gap-4 items-center">
            <div className="flex gap-2 items-center w-[29.1875rem] max-md:shrink-0">
              <CalendarIcon />
              <span className="text-xs font-normal text-auth-muted leading-4">
                {t('proposals.offerDetails.created')}
              </span>
            </div>
            <div className="flex gap-2 items-center w-[7.5rem]">
              <span className="text-xs font-normal text-auth-ink leading-4">
                {getTimeAgo(proposal.createdAt, t)}
              </span>
            </div>
          </div>

          {/* Updated Row */}
          <div className="flex gap-4 items-center">
            <div className="flex gap-2 items-center w-[29.1875rem] max-md:shrink-0">
              <PenIcon />
              <span className="text-xs font-normal text-auth-muted leading-4">
                {t('proposals.offerDetails.updated')}
              </span>
            </div>
            <div className="flex gap-2 items-center">
              <span className="text-xs font-normal text-auth-ink leading-4 w-[7.5rem]">
                {getTimeAgo(proposal.updatedAt, t)}
              </span>
            </div>
          </div>
        </div>

        {/* Close Button */}
        <Button
          onPress={onClose}
          className="bg-primary text-white h-8 px-3 rounded-2 text-xs font-normal leading-4"
        >
          {t('proposals.partnerDetails.close')}
        </Button>
      </div>
    </div>
  );
};

export default OfferTab;
